import { useQuery } from "@tanstack/react-query";
import type { GmvBreakdownLens } from "@unbox/api";
import { handleError } from "@/api/error";
import { client, useScopedMerchantClient } from "./scoped-merchant-client";

type Range = { from: string; to: string };

export const useHomeDistribution = (range: Range, options?: { enabled?: boolean }) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["home-distribution", scope.merchantId, scope.workspaceId, range.from, range.to],
    queryFn: async () => {
      const query = { from: range.from, to: range.to };
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].home.distribution.$get({
            param: scope.param,
            query,
          })
        : await client.merchant[":merchantId"].home.distribution.$get({ param: scope.param, query });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId && (options?.enabled ?? true),
  });
};

export const useHomeSummary = (range: Range, options?: { enabled?: boolean }) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["home-summary", scope.merchantId, scope.workspaceId, range.from, range.to],
    queryFn: async () => {
      const query = { from: range.from, to: range.to };
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].home.summary.$get({
            param: scope.param,
            query,
          })
        : await client.merchant[":merchantId"].home.summary.$get({ param: scope.param, query });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId && (options?.enabled ?? true),
  });
};

export const useHomeGmvBreakdown = (range: Range, lens: GmvBreakdownLens, options?: { enabled?: boolean }) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["home-gmv-breakdown", scope.merchantId, scope.workspaceId, range.from, range.to, lens],
    queryFn: async () => {
      const query = { from: range.from, to: range.to, lens };
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].home.distribution["gmv-breakdown"].$get({
            param: scope.param,
            query,
          })
        : await client.merchant[":merchantId"].home.distribution["gmv-breakdown"].$get({
            param: scope.param,
            query,
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId && (options?.enabled ?? true),
    // Switching lens changes the key, so without this the card would fall back to its
    // loading state and collapse its own header on every tab click.
    placeholderData: (previous) => previous,
  });
};
